import { Injectable } from '@angular/core';
import { AiChatApiService, InitThreadResponse, PromptResponse, RestoreThreadResponse, SuggestionsResponse } from '@panaya/ai-chat/domain';
import { Observable } from 'rxjs';

/**
 * A feature's own answers for the chat window, in place of the standard Seemore copilot.
 * Register with {@link AiChatRouterService.activate} while the feature owns the conversation.
 */
export interface AiChatBackend {
    initThread(): Observable<InitThreadResponse>;
    getSuggestions(): Observable<SuggestionsResponse>;
    restoreThread(threadId: string): Observable<RestoreThreadResponse>;
    /** `contextExtras` are this prompt's own request-context fields, such as the chat's `attachments`. */
    prompt(threadId: string | null, userMessage: string, contextExtras?: Record<string, unknown>): Observable<PromptResponse>;
}

/**
 * Routes the chat window to whichever feature currently owns the conversation, and to the standard
 * Seemore copilot when none does.
 *
 * The chat panel lives in the app shell, so it resolves `AiChatApiService` from the root injector -
 * a feature module cannot provide its own. This service is what the app provides in its place, so a
 * feature plugs itself in by calling {@link activate}, without the app module having to know it
 * exists. There is a single chat panel, so only one backend can own the conversation: the last one
 * to activate wins, and {@link deactivate} hands the window back to the copilot.
 *
 * Only the four conversation calls are routed; everything else on the API (feedback, interrupt)
 * stays with the copilot implementation this service inherits.
 */
@Injectable({ providedIn: 'root' })
export class AiChatRouterService extends AiChatApiService {
    private backend: AiChatBackend | null = null;

    get hasBackend(): boolean {
        return this.backend !== null;
    }

    activate(backend: AiChatBackend): void {
        this.backend = backend;
    }

    /** Ignored when another feature has since taken the window, so a late exit cannot steal it back. */
    deactivate(backend: AiChatBackend): void {
        if (this.backend === backend) {
            this.backend = null;
        }
    }

    override initThread(): Observable<InitThreadResponse> {
        return this.backend ? this.backend.initThread() : super.initThread();
    }

    override getSuggestions(): Observable<SuggestionsResponse> {
        return this.backend ? this.backend.getSuggestions() : super.getSuggestions();
    }

    override restoreThread(threadId: string): Observable<RestoreThreadResponse> {
        return this.backend ? this.backend.restoreThread(threadId) : super.restoreThread(threadId);
    }

    public override prompt(
        threadId: string | null,
        userMessage: string,
        contextExtras?: Record<string, unknown>
    ): Observable<PromptResponse> {
        return this.backend
            ? this.backend.prompt(threadId, userMessage, contextExtras)
            : super.prompt(threadId, userMessage, contextExtras);
    }
}
